import { useEffect, useRef, useState } from 'react'
import { Stack, TextField, Typography } from '@mui/material'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import BomComparisonView from '../bomLibrary/BomComparisonView'
import { previewWorkflowBomPublish, publishWorkflowBom, readBomLibraryError } from '../bomLibrary/bomLibraryApi'

/** Delay between the last keystroke on the code and the preview request (N22). */
const PREVIEW_DEBOUNCE_MS = 400

/**
 * PublishBomDialog — S101/S102 (HU-06/CA6.2, CA6.3): publishes the BOM of the workflow being edited
 * into the library, with the look of the rest of the editor's dialogs (`SmartDialog`, CANCEL / PUBLISH).
 *
 * Code and name are TYPED BY HAND (RN-06.3/D20): nothing is proposed, nothing is derived from the
 * workflow. The preview (N22) runs BY ITSELF while the code is typed and is what decides which of
 * the two outcomes is on offer, with no third one (RN-G6/D14):
 *
 * - `codeExists: false` → a brand new BOM at version 1; no comparison to show.
 * - `codeExists: true`  → the existing name and current version n, the comparison of the workflow's
 *   BOM against it, and the statement that version n+1 will be created. Publishing a second BOM
 *   under the same code is not reachable from here (and Sun would reject it with E-BOM-03).
 *
 * PUBLISH always acts on a preview of the code as typed: if the preview is still pending (or was
 * withdrawn by a later keystroke) it is requested again before publishing, so what is published is
 * exactly what the preview said.
 *
 * The temp scope is only sent when the editor actually carries a temp load (contrato-sun.md §7,
 * deviation 2): with no import pending, publishing must use the persisted structure.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {number} props.workflowId
 * @param {string} props.tempWorkflowUUID the editor's temp scope
 * @param {boolean} props.hasWorkflowResources whether there is a temp load to publish
 * @param {string} props.initialCode preloaded code (S113: the linked BOM's code)
 * @param {string} props.initialName preloaded name (S113: the linked BOM's name)
 * @param {function(object)} props.onPublished called with the `BomPublishResult`
 * @param {function()} props.onCancel
 */
const PublishBomDialog = ({
  open = false,
  workflowId = 0,
  tempWorkflowUUID = '',
  hasWorkflowResources = false,
  initialCode = '',
  initialName = '',
  onPublished = () => {},
  onCancel = () => {}
}) => {
  const { t_ } = useSmartTranslation()
  const [code, setCode] = useState(initialCode)
  const [name, setName] = useState(initialName)
  const [description, setDescription] = useState('')
  // `preview.forCode` records which code the preview answers for, so a stale answer is never used.
  const [preview, setPreview] = useState({})
  const [errorMessage, setErrorMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const latestCode = useRef(initialCode)

  // contrato-sun.md §7 (deviation 2): the temp scope tells Sun which import the editor is looking
  // at. Without a temp load there is none to point at, and publishing uses the persisted structure.
  const publishScope = hasWorkflowResources ? tempWorkflowUUID : ''

  const requestPreview = async (forCode) => {
    const result = await previewWorkflowBomPublish(workflowId, forCode, publishScope)
    // A later keystroke may have changed the code while N22 was in flight: that answer is stale.
    if (latestCode.current !== forCode) return {}
    const nextPreview = { ...(result || {}), forCode }
    setPreview(nextPreview)
    // RN-06.4: the name of an existing BOM is the library's, not something the user re-invents.
    if (nextPreview.codeExists && nextPreview.name) setName((current) => current || nextPreview.name)
    return nextPreview
  }

  useEffect(() => {
    latestCode.current = code
    setPreview({})
    setErrorMessage('')
    if (!code) return undefined
    const timer = setTimeout(() => {
      requestPreview(code).catch((thrown) => setErrorMessage(readBomLibraryError(thrown).error))
    }, PREVIEW_DEBOUNCE_MS)
    return () => clearTimeout(timer)
    // `requestPreview` closes over stable props; re-running on every render would re-request N22.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code])

  const publish = async () => {
    if (busy || !code || !name) return
    setBusy(true)
    setErrorMessage('')
    try {
      const current = preview.forCode === code ? preview : await requestPreview(code)
      const result = await publishWorkflowBom(
        workflowId,
        {
          code,
          name,
          description,
          // `asNewVersionOf` is the previewed BOM: it is what turns "publish" into "create version
          // n+1 of THAT BOM" instead of the E-BOM-03 rejection of a duplicated code.
          asNewVersionOf: current.codeExists ? current.bomId : null
        },
        publishScope
      )
      onPublished(result)
    } catch (thrown) {
      setErrorMessage(readBomLibraryError(thrown).error)
    }
    setBusy(false)
  }

  const previewed = Boolean(code) && preview.forCode === code
  const nextVersion = Number(preview.currentVersionNumber || 0) + 1

  return (
    <SmartDialog
      setOpen={open}
      title={{ render: <Typography variant='h6'>{t_('Publish to library')}</Typography> }}
      acceptLabel='Publish'
      acceptCallback={publish}
      cancelCallback={onCancel}
      disableButtons={busy}
      autoClose={false}
      paperWidth='60rem'
      renderComponent={
        <Stack spacing={2} sx={{ textAlign: 'left', minWidth: 320 }} data-testid='publish-bom-dialog'>
          <TextField
            size='small'
            label={t_('Code')}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            data-testid='publish-bom-code'
          />
          <TextField
            size='small'
            label={t_('Name')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            data-testid='publish-bom-name'
          />
          <TextField
            size='small'
            label={t_('Description')}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            data-testid='publish-bom-description'
          />
          {previewed
            ? (
              <Stack spacing={2} data-testid='publish-bom-preview-result'>
                {preview.codeExists
                  ? (
                    <>
                      <Typography variant='subtitle2'>{preview.name}</Typography>
                      <Typography variant='body2'>{t_('Current version')} {preview.currentVersionNumber}</Typography>
                      <Typography variant='body2'>
                        {t_('Version {{version}} of {{code}} will be created and this workflow will point to it', { version: nextVersion, code })}
                      </Typography>
                      <BomComparisonView comparison={preview.comparison} />
                    </>
                    )
                  : <Typography variant='body2'>{t_('A new BOM will be created (version 1)')}</Typography>}
              </Stack>
              )
            : null}
          {errorMessage ? <Typography color='error.main' data-testid='publish-bom-error'>{t_(errorMessage)}</Typography> : null}
        </Stack>
      }
    />
  )
}

export default PublishBomDialog
